// modules/InspectionItemField.jsx
import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  FormLabel,
  FormHelperText,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import ImageIcon from "@mui/icons-material/Image";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import axios from "axios";

const API_BASE_URL = "https://formbuilderapi.getforsite.com";

const CRIT_COLORS = {
  High:   "#db441f",
  Medium: "#f59c16",
  Low:    "#80cc5a",
};

const normalizeAuditCrit = (ac) => ({
  High:   Array.isArray(ac?.High)   ? ac.High   : [],
  Medium: Array.isArray(ac?.Medium) ? ac.Medium : [],
  Low:    Array.isArray(ac?.Low)    ? ac.Low    : [],
});

const getOptionCritLevel = (module, option) => {
  const { High, Medium, Low } = normalizeAuditCrit(module?.auditCriticality);
  if (High.includes(option))   return "High";
  if (Medium.includes(option)) return "Medium";
  if (Low.includes(option))    return "Low";
  return null;
};

const normalizeValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return {
      selection: value.selection ?? null,
      notes:     value.notes ?? "",
      media:     Array.isArray(value.media) ? value.media : [],
    };
  }
  if (typeof value === "string") {
    return { selection: value || null, notes: "", media: [] };
  }
  return { selection: null, notes: "", media: [] };
};

const ALLOWED_EXTENSIONS = [".jpeg", ".jpg", ".png", ".gif", ".webp"];
const ALLOWED_MIME_TYPES = [
  "image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp",
];
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

const DEFAULT_OPTIONS = ["Safe", "At Risk", "N/A"];

const InspectionItemField = ({
  module,
  value,
  isEditable,
  onChange,
  fieldKey,
  formId,
}) => {
  const [showNotes, setShowNotes] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // "2 / 4"

  const current   = normalizeValue(value);
  const { selection, notes, media } = current;
  const options   = module.options || DEFAULT_OPTIONS;

  // ── mandatory-fail detection ─────────────────────────────────────────────
  const { High } = normalizeAuditCrit(module?.auditCriticality);
  const hasMandatoryFail = High.length > 0 && selection && High.includes(selection);

  const handleSelect = (option) => {
    if (!isEditable) return;
    const newSelection = selection === option ? null : option;
    onChange(fieldKey, { ...current, selection: newSelection });
  };

  const handleNotesChange = (e) => {
    onChange(fieldKey, { ...current, notes: e.target.value });
  };

  // Validate a single File; returns an error string or null
  const validateFile = (file) => {
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext) || !ALLOWED_MIME_TYPES.includes(file.type.toLowerCase())) {
      return `"${file.name}" is an unsupported file type. Please upload JPEG, PNG, GIF, or WebP files only.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `"${file.name}" exceeds the 15 MB size limit.`;
    }
    return null;
  };

  // Upload a single file to the API and return its URL
  const uploadSingleFile = async (file) => {
    const formData = new FormData();
    formData.append(`documents[${fieldKey}]`, file, file.name);
    const res = await axios.post(
      `${API_BASE_URL}/api/forms/${formId}/upload-documents`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return res.data.uploadedUrls[fieldKey];
  };

  const handleMediaUpload = async (files) => {
    if (!files || files.length === 0) return;
    if (!formId) {
      console.warn("InspectionItemField: formId not provided, cannot upload.");
      return;
    }

    // Validate everything up-front before any network requests
    for (const file of files) {
      const err = validateFile(file);
      if (err) { alert(err); return; }
    }

    const total = files.length;
    let done = 0;

    try {
      setUploading(true);
      setUploadProgress(total > 1 ? `0 / ${total}` : null);

      // Upload all files in parallel
      const uploadedUrls = await Promise.all(
        Array.from(files).map(async (file) => {
          const url = await uploadSingleFile(file);
          done += 1;
          if (total > 1) setUploadProgress(`${done} / ${total}`);
          return url;
        }),
      );

      // Merge new URLs into the existing media array in one onChange call
      onChange(fieldKey, { ...current, media: [...media, ...uploadedUrls] });
    } catch (err) {
      console.error("Error uploading inspection media:", err);
      alert("An error occurred while uploading one or more photos.");
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleRemoveMedia = (idx) => {
    const updated = media.filter((_, i) => i !== idx);
    onChange(fieldKey, { ...current, media: updated });
  };

  const hasNotes = notes && notes.trim().length > 0;
  const hasMedia = media.length > 0;

  const uploadLabel = uploading
    ? uploadProgress
      ? `Uploading ${uploadProgress}…`
      : "Uploading…"
    : "Upload Photos";

  return (
    <Box
      sx={{
        py: 1.5,
        borderBottom: "1px solid #e8e8e8",
        "&:last-child": { borderBottom: "none" },
        borderLeft: hasMandatoryFail ? "4px solid #d00000" : undefined,
        pl:         hasMandatoryFail ? 1.5 : undefined,
        transition: "border-left 0.2s ease, padding-left 0.2s ease",
      }}
    >
      {/* Label row — weight badge if present */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
        <FormLabel
          component="legend"
          sx={{
            flex: 1,
            display: "block",
            fontWeight: 500,
            color: "text.primary",
            fontSize: "0.9rem",
          }}
        >
          {module.label}
          {module.required && (
            <Typography component="span" color="error" sx={{ ml: 0.25 }}>
              *
            </Typography>
          )}
        </FormLabel>
        {typeof module.weight === "number" && module.weight > 0 && (
          <Box
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "#fff",
              bgcolor: "#5c6bc0",
              borderRadius: 99,
              px: 1,
              py: 0.25,
              whiteSpace: "nowrap",
              lineHeight: 1.4,
              mt: 0.25,
              flex: "0 0 auto",
            }}
          >
            {module.weight} pts
          </Box>
        )}
      </Box>

      {/* Horizontal option pills */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
        {options.map((option, idx) => {
          const level      = getOptionCritLevel(module, option);
          const isSelected = selection === option;
          const color      = level ? CRIT_COLORS[level] : "#5c6bc0";

          return (
            <Box
              key={idx}
              component="button"
              onClick={() => handleSelect(option)}
              disabled={!isEditable}
              sx={{
                display:         "inline-flex",
                alignItems:      "center",
                gap:             0.5,
                border:          `1.5px solid ${color}`,
                borderRadius:    6,
                px:              2,
                py:              0.6,
                fontSize:        "0.85rem",
                fontWeight:      isSelected ? 600 : 400,
                cursor:          isEditable ? "pointer" : "default",
                backgroundColor: isSelected ? color : "transparent",
                color:           isSelected ? "#fff" : color,
                transition:      "all 0.15s ease",
                outline:         "none",
                fontFamily:      "inherit",
                "&:hover": isEditable
                  ? { backgroundColor: isSelected ? color : `${color}18` }
                  : {},
                "&:disabled": { opacity: 0.85, cursor: "default" },
              }}
            >
              {option}
            </Box>
          );
        })}
      </Box>

      {/* Mandatory-fail inline alert */}
      {hasMandatoryFail && (
        <Alert
          severity="error"
          icon={<WarningAmberIcon />}
          sx={{
            mb: 1,
            fontWeight: 600,
            border: "1.5px solid #d00000",
            "& .MuiAlert-message": { fontSize: "0.83rem" },
          }}
        >
          <strong>Critical Risk — Automatic FAIL</strong>
          <br />
          This answer identifies a critical risk item. The audit will be
          recorded as <strong>FAIL</strong> regardless of other responses.
        </Alert>
      )}

      {/* Note / Media toggle row */}
      <Box sx={{ display: "flex", gap: 2, mt: 0.75 }}>
        <Button
          size="small"
          startIcon={<NoteAltIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => setShowNotes((v) => !v)}
          sx={{
            textTransform: "none",
            fontSize:      "0.76rem",
            color:         hasNotes ? "primary.main" : "text.secondary",
            p:             0,
            minWidth:      0,
            fontWeight:    hasNotes ? 600 : 400,
          }}
        >
          Note{hasNotes ? " ✓" : ""}
        </Button>
        <Button
          size="small"
          startIcon={<ImageIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => setShowMedia((v) => !v)}
          sx={{
            textTransform: "none",
            fontSize:      "0.76rem",
            color:         hasMedia ? "primary.main" : "text.secondary",
            p:             0,
            minWidth:      0,
            fontWeight:    hasMedia ? 600 : 400,
          }}
        >
          Media{hasMedia ? ` (${media.length})` : ""}
        </Button>
      </Box>

      {/* Notes field */}
      {showNotes && (
        <TextField
          value={notes || ""}
          onChange={handleNotesChange}
          fullWidth
          multiline
          rows={2}
          placeholder="Add a note..."
          size="small"
          disabled={!isEditable}
          sx={{ mt: 1 }}
        />
      )}

      {/* Media section */}
      {showMedia && (
        <Box sx={{ mt: 1 }}>
          {hasMedia && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
              {media.map((url, i) => (
                <Box key={i} sx={{ position: "relative" }}>
                  <img
                    src={url}
                    alt={`Media ${i + 1}`}
                    loading="lazy"
                    style={{
                      width:        80,
                      height:       80,
                      objectFit:    "cover",
                      borderRadius: 6,
                      display:      "block",
                    }}
                  />
                  {isEditable && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveMedia(i)}
                      sx={{
                        position: "absolute",
                        top:      -6,
                        right:    -6,
                        bgcolor:  "error.main",
                        color:    "#fff",
                        p:        0.25,
                        "&:hover": { bgcolor: "error.dark" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {isEditable && (
            <Button
              variant="outlined"
              size="small"
              startIcon={
                uploading
                  ? <CircularProgress size={12} />
                  : <CloudUploadIcon fontSize="small" />
              }
              component="label"
              disabled={uploading}
              sx={{ textTransform: "none", fontSize: "0.78rem" }}
            >
              {uploadLabel}
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                multiple
                hidden
                onChange={(e) => handleMediaUpload(e.target.files)}
              />
            </Button>
          )}
        </Box>
      )}

      {module.required && !selection && (
        <FormHelperText error sx={{ mt: 0.5 }}>
          Please select an option
        </FormHelperText>
      )}
    </Box>
  );
};

export default InspectionItemField;